import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getMoonPhasesForMonth } from "@/utils/moonPhase";
import { ortaggi } from "@/data/ortaggi";

interface CalendarioMensileProps {
  selectedMonth: number;
  selectedYear: number;
  onMonthChange: (month: number, year: number) => void;
}

const MESI = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"
];

const CalendarioMensile = ({ selectedMonth, selectedYear, onMonthChange }: CalendarioMensileProps) => {
  const currentDate = new Date();
  const isCurrentMonth = selectedMonth === currentDate.getMonth() && selectedYear === currentDate.getFullYear();
  
  const moonPhases = getMoonPhasesForMonth(selectedYear, selectedMonth);

  // Trova ortaggi da seminare in questo mese (ordinati alfabeticamente)
  const ortaggiDaSeminare = ortaggi
    .filter(o => o.mesiSemina.includes(selectedMonth + 1))
    .sort((a, b) => a.nome.localeCompare(b.nome));

  // Trova ortaggi da raccogliere in questo mese (ordinati alfabeticamente)
  const ortaggiDaRaccogliere = ortaggi
    .filter(o => o.mesiRaccolta.includes(selectedMonth + 1))
    .sort((a, b) => a.nome.localeCompare(b.nome));
  
  const handlePrevMonth = () => {
    if (selectedMonth === 0) {
      onMonthChange(11, selectedYear - 1);
    } else {
      onMonthChange(selectedMonth - 1, selectedYear);
    }
  };
  
  const handleNextMonth = () => {
    if (selectedMonth === 11) {
      onMonthChange(0, selectedYear + 1);
    } else {
      onMonthChange(selectedMonth + 1, selectedYear);
    }
  };
  
  // Trova le principali fasi lunari del mese
  const mainMoonPhases = moonPhases.filter((phase, index) => {
    if (index === 0) return true;
    const prevPhase = moonPhases[index - 1];
    return phase.type !== prevPhase.type;
  });
  
  return (
    <div className="bg-card border border-border rounded-xl p-6 shadow-lg">
      {/* Header con navigazione */}
      <div className="flex items-center justify-between mb-6">
        <Button
          variant="outline"
          size="icon"
          onClick={handlePrevMonth}
          className="hover:bg-primary/10"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        
        <div className="text-center">
          <h2 className={`text-2xl font-bold ${isCurrentMonth ? 'text-primary' : 'text-foreground'}`}>
            {MESI[selectedMonth]} {selectedYear}
          </h2>
          {isCurrentMonth && (
            <p className="text-sm text-muted-foreground mt-1">Mese corrente</p>
          )}
        </div>
        
        <Button
          variant="outline"
          size="icon"
          onClick={handleNextMonth}
          className="hover:bg-primary/10"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      
      {/* Ortaggi da seminare e raccogliere */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Da seminare */}
        <div>
          <h3 className="text-sm font-semibold text-muted-foreground mb-3">
            Da seminare questo mese:
          </h3>
          <div className="flex flex-wrap gap-2">
            {ortaggiDaSeminare.length > 0 ? (
              ortaggiDaSeminare.map(ortaggio => (
                <div
                  key={`semina-${ortaggio.id}`}
                  className="flex items-center gap-1 bg-primary/10 px-3 py-1.5 rounded-full border border-primary/20"
                  title={ortaggio.nome}
                >
                  <span className="text-lg">{ortaggio.icona}</span>
                  <span className="text-xs font-medium text-foreground">{ortaggio.nome}</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground italic">
                Nessun ortaggio da seminare
              </p>
            )}
          </div>
        </div>

        {/* Da raccogliere */}
        <div>
          <h3 className="text-sm font-semibold text-muted-foreground mb-3">
            Da raccogliere questo mese:
          </h3>
          <div className="flex flex-wrap gap-2">
            {ortaggiDaRaccogliere.length > 0 ? (
              ortaggiDaRaccogliere.map(ortaggio => (
                <div
                  key={`raccolta-${ortaggio.id}`}
                  className="flex items-center gap-1 bg-green-500/10 px-3 py-1.5 rounded-full border border-green-500/20"
                  title={ortaggio.nome}
                >
                  <span className="text-lg">{ortaggio.icona}</span>
                  <span className="text-xs font-medium text-foreground">{ortaggio.nome}</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground italic">
                Nessun ortaggio da raccogliere
              </p>
            )}
          </div>
        </div>
      </div>
      
      {/* Fasi lunari principali */}
      <div className="border-t border-border pt-4">
        <h3 className="text-sm font-semibold text-muted-foreground mb-3">
          Fasi lunari del mese:
        </h3>
        <div className="flex flex-wrap gap-3">
          {mainMoonPhases.map((phase, index) => (
            <div
              key={index}
              className="flex items-center gap-2 bg-gradient-to-br from-blue-900/10 to-purple-900/10 px-3 py-2 rounded-lg border border-blue-800/20"
            >
              <span className="text-2xl">{phase.icon}</span>
              <div className="text-left">
                <p className="text-xs font-medium text-foreground">{phase.name}</p>
                <p className="text-xs text-muted-foreground">
                  {phase.type === 'crescente' && 'Frutto/Foglia'}
                  {phase.type === 'calante' && 'Radice/Bulbo'}
                  {(phase.type === 'nuova' || phase.type === 'piena') && 'Transizione'}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CalendarioMensile;
